/// <reference lib="es2018" />
/// <reference lib="dom" />

import { writable, derived } from "svelte/store";
import { canOpen, homeRoute, loginDestination, normalizePath, type PageRef, type NavigationUser } from "./routePolicy";

export type Permission = string;
type PermissionSet = Set<Permission>;
export type Route = {
  feature: string; page: string; title: string; href: string;
  modulePath: string; menuModulePath?: string | null;
  showInMenu: boolean; requiredPermissions: string[];
};
export type NavigationResult =
  | { kind: "allowed"; route: Route }
  | { kind: "login" | "forbidden" | "not-found" | "unavailable" | "blocked" | "no-home" };

export const authPhase = writable<"guest" | "authed">("guest");
export const permissions = writable<PermissionSet>(new Set());
const routesVersion = writable(0);
let allRoutes: Route[] = [];
let defaultRoute: PageRef | null = null;
let pendingUser: Promise<NavigationUser> | null = null;

export class AuthRequestError extends Error {
  status: number;
  constructor(status: number) {
    super(status === 401 ? "unauthenticated" : `user fetch failed: ${status}`);
    // TypeScript's ES5 downleveling of built-in subclasses otherwise loses
    // instanceof identity, causing real 401s to look like network failures.
    Object.setPrototypeOf(this, AuthRequestError.prototype);
    this.status = status;
  }
}

/** Coalesce concurrent checks, but never authorize navigation using cached permissions. */
export function refreshUser(): Promise<NavigationUser> {
  if (pendingUser) return pendingUser;
  pendingUser = (async () => {
    const response = await fetch("/user-authentication/api/user", {
      credentials: "include", cache: "no-store", headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        authPhase.set("guest");
        permissions.set(new Set());
        try { localStorage.removeItem("auth.permissions.v1"); } catch { /* storage unavailable */ }
      }
      throw new AuthRequestError(response.status);
    }
    const data = await response.json();
    const user = data.user ?? data;
    if (!Array.isArray(user.permissions)) throw new Error("Invalid user response");
    permissions.set(new Set(user.permissions));
    authPhase.set("authed");
    window.dispatchEvent(new Event("huckleberry-authenticated"));
    return user as NavigationUser;
  })().finally(() => { pendingUser = null; });
  return pendingUser;
}

export async function refreshPermissions(): Promise<PermissionSet> {
  return new Set((await refreshUser()).permissions);
}

export function initPermissions() {
  // Transient network errors must not turn an authenticated user into a guest.
  void refreshUser().catch(() => {});
}

export function setRoutes(data: { routes: any[]; defaultRoute?: PageRef | null }) {
  defaultRoute = data.defaultRoute ?? null;
  allRoutes = (Array.isArray(data.routes) ? data.routes : []).map(r => ({
    ...r,
    href: normalizePath(String(r.href ?? "/")),
    showInMenu: ![false, "false", 0, "0"].includes(r.showInMenu),
    requiredPermissions: Array.isArray(r.requiredPermissions) ? r.requiredPermissions.map(String) : [],
  }));
  routesVersion.update(n => n + 1);
}

export function routeForPath(path: string): Route | undefined {
  return allRoutes.find(r => r.href === normalizePath(path));
}

export const visibleRoutes = derived([authPhase, permissions, routesVersion], ([$phase, $permissions]) =>
  allRoutes.filter(r => canOpen(r, $phase === "authed" ? Array.from($permissions) : [])));
export const menuRoutes = derived(visibleRoutes, routes => routes.filter(r => r.showInMenu));

function failure(error: unknown): NavigationResult {
  if (error instanceof AuthRequestError && error.status === 401) return { kind: "login" };
  if (error instanceof AuthRequestError && error.status === 403) return { kind: "blocked" };
  return { kind: "unavailable" };
}

export async function resolveNavigation(path: string): Promise<NavigationResult> {
  const normalized = normalizePath(path);
  const route = routeForPath(normalized);
  if (normalized !== "/" && !route) return { kind: "not-found" };
  if (route && !route.requiredPermissions.length) {
    // Public pages remain usable without a session or working network.
    if (route.menuModulePath) initPermissions();
    return { kind: "allowed", route };
  }
  let user: NavigationUser;
  try { user = await refreshUser(); } catch (error) { return failure(error); }
  if (normalized === "/") {
    const home = homeRoute(allRoutes, defaultRoute, user);
    return home ? { kind: "allowed", route: home } : { kind: "no-home" };
  }
  return route && canOpen(route, user.permissions) ? { kind: "allowed", route } : { kind: "forbidden" };
}

export function redirectToLogin(next = location.pathname + location.search + location.hash) {
  const url = new URL(next, location.origin);
  const dest = url.origin === location.origin ? url.pathname + url.search + url.hash : "/";
  location.replace(`/user-authentication/login?next=${encodeURIComponent(dest)}`);
}

/** Boolean guard for callers; only a missing session redirects to login. */
export async function ensureRouteAllowedForPath(path = location.pathname): Promise<boolean> {
  const result = await resolveNavigation(path);
  if (result.kind === "login") redirectToLogin();
  return result.kind === "allowed";
}

/** safePath has already passed safeNext's same-origin and encoding validation. */
export async function destinationAfterLogin(safePath: string) {
  return loginDestination(allRoutes, defaultRoute, await refreshUser(), safePath);
}

export async function requireLogin(next?: string) {
  try { await refreshUser(); } catch (error) {
    if (failure(error).kind === "login") redirectToLogin(next);
    else throw error;
  }
}

export async function requireElevation(next = location.pathname + location.search + location.hash) {
  try { await refreshUser(); } catch (error) {
    if (failure(error).kind === "login") { redirectToLogin(next); return; }
    throw error;
  }
  const response = await fetch("/user-authentication/api/user/sessions", { credentials: "include", cache: "no-store" });
  if (response.status === 401) { redirectToLogin(next); return; }
  if (!response.ok) throw new AuthRequestError(response.status);
  if (!(await response.json()).current_elevated) {
    location.replace(`/user-authentication/elevate?next=${encodeURIComponent(next)}`);
  }
}
